import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  Clock,
  Hourglass,
  Repeat,
  Scale,
  UserX,
  Users,
} from 'lucide-react';
import type {
  AgentWorkloadReport,
  BacklogAgeingReport,
  DemandShapeReport,
  RecurrenceReport,
  ResponseQualityReport,
} from '../../lib/api.js';
import { hours, num, pct, personLabel } from '../../lib/format.js';
import { shortRequestType } from '../../lib/labels.js';
import { Avatar, Badge, Callout, Metric, Panel, Tooltip, cn } from '../../ui/index.js';
import { DataTable, HBar, Heatmap, LineChart, Meter, PairedBars, StackedShare } from '../charts.js';

/**
 * The five detailed report tabs.
 *
 * Each one is deliberately opinionated about which number is the headline and
 * what it means, because a table of everything is not a report — it is a data
 * dump with a title. The prose under each panel is the argument the number
 * supports.
 */

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS_OF_DAY = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}`);

const days = (d: number | null): string =>
  d === null ? '—' : d < 1 ? `${Math.round(d * 24)}h` : `${d.toFixed(d < 10 ? 1 : 0)}d`;

/**
 * The current month, as the reports label it.
 *
 * Every month-by-month figure has a partial last bucket, and a partial bucket
 * read as a complete one is how a dashboard says "attainment 100%" off three
 * tickets, or shows a cliff in volume that is really just the calendar. So the
 * partial month is marked everywhere it appears and excluded from anything
 * that compares periods.
 */
const CURRENT_MONTH = new Date().toISOString().slice(0, 7);
const isPartial = (month: string): boolean => month === CURRENT_MONTH;

// ---------------------------------------------------------------------------
// Workload
// ---------------------------------------------------------------------------

export function WorkloadTab({ data }: { data: AgentWorkloadReport }) {
  const totalOpen = data.rows.reduce((a, r) => a + r.openNow, 0) + data.unassigned.openNow;
  const busiest = [...data.rows].sort((a, b) => b.openNow - a.openNow)[0];
  const gini = data.loadGini;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric
          label="People with open work"
          value={num(data.rows.filter((r) => r.openNow > 0).length)}
          hint={`${num(totalOpen)} open tickets across the team`}
          icon={<Users />}
        />
        <Metric
          label="Load balance"
          value={gini === null ? '—' : gini.toFixed(2)}
          tone={gini !== null && gini > 0.5 ? 'warn' : 'default'}
          hint="Gini of open tickets per person. 0 is even, 1 is one person holding everything."
          icon={<Scale />}
        />
        <Metric
          label="Busiest queue holder"
          value={busiest ? num(busiest.openNow) : '—'}
          hint={busiest ? personLabel(busiest.name, busiest.email) : 'Nobody holds anything'}
          icon={<Hourglass />}
        />
        <Metric
          label="Unassigned"
          value={num(data.unassigned.openNow)}
          tone={data.unassigned.openNow > 0 ? 'warn' : 'ok'}
          hint={
            data.unassigned.oldestOpenDays === null
              ? 'Nothing waiting for an owner'
              : `Oldest has waited ${days(data.unassigned.oldestOpenDays)} for an owner`
          }
          icon={<UserX />}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Open work per person"
          subtitle="What each agent is holding right now, regardless of when it arrived"
          footer={
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              Current load and window throughput are different questions. Somebody can have
              resolved nothing this month and still be carrying forty tickets — which is a
              conversation about their queue, not their pace.
            </p>
          }
        >
          <HBar
            rows={[...data.rows]
              .filter((r) => r.openNow > 0)
              .sort((a, b) => b.openNow - a.openNow)
              .slice(0, 15)
              .map((r) => ({
                label: personLabel(r.name, r.email),
                value: r.openNow,
                hint: `${r.openNow} open · ${r.blockedNow} blocked · oldest ${days(r.oldestOpenDays)}`,
                // Through to the tickets themselves. Seeing that somebody is
                // holding forty and having no way to open them is where this
                // screen stopped being useful.
                ...(r.oid ? { href: `/queue?assignee=${encodeURIComponent(r.oid)}` } : {}),
              }))}
            valueLabel={(v) => num(v)}
          />
        </Panel>

        <Panel
          title="Throughput and outcome"
          subtitle="Resolved in the window, and how much of it missed its target"
        >
          <DataTable
            head={['Agent', 'Resolved', 'Median', 'p90', 'Overdue', 'No human reply']}
            rows={[...data.rows]
              .filter((r) => r.resolved > 0)
              .sort((a, b) => b.resolved - a.resolved)
              .slice(0, 15)
              .map((r) => [
                <span key="a" className="flex items-center gap-1.5">
                  <Avatar name={r.name} email={r.email} size="xs" />
                  <span className="truncate">{personLabel(r.name, r.email)}</span>
                </span>,
                num(r.resolved),
                hours(r.medianHours),
                hours(r.p90Hours),
                <span key="b" className="inline-flex items-center gap-1.5">
                  <Meter
                    value={r.breachRate}
                    tone={
                      r.breachRate !== null && r.breachRate > 0.3
                        ? 'danger'
                        : r.breachRate !== null && r.breachRate > 0.15
                          ? 'warn'
                          : 'default'
                    }
                  />
                </span>,
                pct(r.untouchedShare),
              ])}
          />
        </Panel>
      </div>

      {gini !== null && gini > 0.5 && (
        <Callout tone="warn" icon={<AlertTriangle />}>
          Open work is concentrated: a Gini of {gini.toFixed(2)} means a small number of people are
          holding most of the queue. That is a routing and capacity question before it is a
          performance one.
        </Callout>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Backlog
// ---------------------------------------------------------------------------

export function BacklogTab({
  data,
  tenant,
}: {
  data: BacklogAgeingReport;
  tenant: string;
}) {
  const total = data.buckets.reduce((a, b) => a + b.tickets, 0);
  const overAWeek = data.buckets.slice(3).reduce((a, b) => a + b.tickets, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label="Open backlog" value={num(total)} icon={<Clock />} />
        <Metric
          label="Older than a week"
          value={num(overAWeek)}
          tone={total > 0 && overAWeek / total > 0.3 ? 'warn' : 'default'}
          hint={total > 0 ? `${pct(overAWeek / total)} of everything open` : undefined}
          icon={<Hourglass />}
        />
        <Metric
          label="Never answered"
          value={num(data.neverAnswered.tickets)}
          tone={data.neverAnswered.tickets > 0 ? 'danger' : 'ok'}
          hint={
            data.neverAnswered.medianAgeDays === null
              ? 'Everything open has had at least one reply'
              : `Median wait ${days(data.neverAnswered.medianAgeDays)} with no reply at all`
          }
          icon={<AlertTriangle />}
        />
        <Metric
          label="Unassigned"
          value={num(data.buckets.reduce((a, b) => a + b.unassigned, 0))}
          icon={<UserX />}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Backlog by age"
          subtitle="How long the open work has been open"
          footer={
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              One "open backlog" number hides the shape entirely. A hundred tickets from this
              morning and a hundred from last quarter are the same figure and completely different
              problems.
            </p>
          }
        >
          <HBar
            rows={data.buckets.map((b) => ({
              label: b.label,
              value: b.tickets,
              hint: `${b.tickets} open · ${b.breached} past target · ${b.unassigned} unassigned`,
            }))}
            valueLabel={(v) => num(v)}
          />
          <div className="mt-3 border-t border-line-subtle pt-3">
            <StackedShare
              segments={data.buckets
                .filter((b) => b.tickets > 0)
                .map((b) => ({ label: b.label, value: b.tickets }))}
              formatValue={(v) => num(v)}
            />
          </div>
        </Panel>

        <Panel title="By queue" subtitle="Where the old work is sitting">
          <DataTable
            head={['Queue', '<1d', '1–3d', '3–7d', '1–4w', '>1m', 'Total']}
            rows={data.byQueue.map((q) => [
              q.queue,
              ...q.counts.map((c, i) => (
                <span key={i} className={cn(c > 0 && i >= 3 && 'font-semibold text-warn')}>
                  {c || '—'}
                </span>
              )),
              <span key="t" className="font-semibold">
                {q.total}
              </span>,
            ])}
          />
        </Panel>
      </div>

      <Panel
        title="The oldest open tickets"
        subtitle="Oldest first — this is the list somebody should be working from"
      >
        <DataTable
          head={['Ticket', 'Subject', 'Type', 'Queue', 'Owner', 'Age', '']}
          rows={data.oldest.map((t) => [
            <Link
              key="n"
              to={`/t/${tenant}/${t.number}`}
              className="nums font-medium text-brand hover:underline"
            >
              #{t.number}
            </Link>,
            <span key="s" className="line-clamp-1 max-w-[24rem]">
              {t.subject}
            </span>,
            shortRequestType(t.requestType ?? 'Unclassified'),
            t.queue ?? '—',
            t.assignee ?? (
              <span key="u" className="text-warn">
                unassigned
              </span>
            ),
            <span key="a" className="nums font-medium">
              {days(t.ageDays)}
            </span>,
            t.breached ? (
              <Badge key="b" tone="danger">
                past target
              </Badge>
            ) : (
              ''
            ),
          ])}
        />
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Demand
// ---------------------------------------------------------------------------

export function DemandTab({ data }: { data: DemandShapeReport }) {
  const peakCell = Math.max(...data.heatmap.flat());
  const peak = (() => {
    for (let d = 0; d < 7; d++) {
      for (let h = 0; h < 24; h++) {
        if (data.heatmap[d]![h] === peakCell) return `${DOW[d]} ${String(h).padStart(2, '0')}:00`;
      }
    }
    return '—';
  })();
  // Complete months only. Comparing a half-finished month against three full
  // ones reports a collapse in demand that is really just the calendar.
  const monthly = data.monthly;
  const complete = monthly.filter((m) => !isPartial(m.month));
  const trend =
    complete.length >= 6
      ? complete.slice(-3).reduce((a, m) => a + m.created, 0) -
        complete.slice(-6, -3).reduce((a, m) => a + m.created, 0)
      : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric
          label="Busiest hour"
          value={peak}
          hint={`${num(peakCell)} arrivals in that hour across the window`}
          icon={<CalendarClock />}
        />
        <Metric
          label="Out of hours"
          value={pct(data.outOfHours.share)}
          tone={
            data.outOfHours.share !== null && data.outOfHours.share > 0.2 ? 'warn' : 'default'
          }
          hint={`${num(data.outOfHours.tickets)} arrived outside 08:00–17:00 Mon–Fri`}
          icon={<Clock />}
        />
        <Metric
          label="Distinct systems"
          value={num(data.systems.length)}
          hint="Named in the classification, not typed in by hand"
          icon={<Users />}
        />
        <Metric
          label="Quarter on quarter"
          value={trend === null ? '—' : trend === 0 ? 'flat' : `${trend > 0 ? '+' : ''}${num(trend)}`}
          tone={trend !== null && trend > 0 ? 'warn' : 'default'}
          hint={
            trend === null
              ? 'Needs six complete months to compare'
              : 'Last three complete months of arrivals against the three before. The month in progress is excluded.'
          }
          icon={<Repeat />}
        />
      </div>

      <Panel
        title="When the work arrives"
        subtitle="Arrivals by day and hour, in the desk's own timezone"
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            This is the staffing evidence. Local time, not UTC — an hour-of-day chart drawn in UTC
            is off by seven or eight and quietly wrong, which makes it worse than no chart.
          </p>
        }
      >
        <Heatmap
          grid={data.heatmap}
          rowLabels={DOW}
          colLabels={HOURS_OF_DAY}
          valueLabel={(v) => `${num(v)} tickets`}
        />
      </Panel>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Volume by month"
          subtitle="Arrivals against closures"
          footer={
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              The current month is marked “so far” — it is a partial bucket, and reading it as a
              complete one turns every month-end into an apparent collapse in demand.
            </p>
          }
        >
          <PairedBars
            rows={monthly.map((m) => ({
              label: isPartial(m.month) ? `${m.month.slice(2)} so far` : m.month.slice(2),
              a: m.created,
              b: m.resolved,
            }))}
            labelA="Arrived"
            labelB="Resolved"
            format={(v) => num(v)}
          />
        </Panel>

        <Panel
          title="Systems named"
          subtitle="What the work is actually about, with the breach rate behind it"
        >
          <HBar
            rows={data.systems.map((s) => ({
              label: s.system,
              value: s.tickets,
              hint: `${s.tickets} tickets · ${pct(s.breachRate)} overdue`,
            }))}
            valueLabel={(v) => num(v)}
            emphasise={(label) =>
              (data.systems.find((s) => s.system === label)?.breachRate ?? 0) > 0.3
            }
          />
        </Panel>

        <Panel title="Who is asking" subtitle="By the requester's department">
          <DataTable
            head={['Department', 'Tickets', 'People', 'Per person']}
            rows={data.departments.map((d) => [
              d.department,
              num(d.tickets),
              num(d.requesters),
              d.requesters > 0 ? (d.tickets / d.requesters).toFixed(1) : '—',
            ])}
          />
        </Panel>

        <Panel title="Where from" subtitle="By the requester's office">
          <HBar
            rows={data.offices.map((o) => ({ label: o.office, value: o.tickets }))}
            valueLabel={(v) => num(v)}
          />
        </Panel>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quality
// ---------------------------------------------------------------------------

export function QualityTab({ data }: { data: ResponseQualityReport }) {
  // The last COMPLETE month with a real sample. Reading the month in progress
  // as a headline turned three closed tickets into "100% attainment".
  const latest = [...data.monthlyAttainment]
    .reverse()
    .find((m) => m.measured > 0 && !isPartial(m.month));
  const pausedPct = data.pausedShare.medianPausedShare;
  const hasPauseData = data.pausedShare.withPause > 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric
          label="First response"
          value={hours(data.firstResponse.median)}
          hint={`p90 ${hours(data.firstResponse.p90)} over ${num(data.firstResponse.n)} tickets`}
          icon={<Clock />}
        />
        <Metric
          label="Resolution"
          value={hours(data.resolution.median)}
          hint={`p90 ${hours(data.resolution.p90)} · tail ratio ${
            data.resolution.tailRatio === null ? '—' : `${data.resolution.tailRatio.toFixed(1)}×`
          }`}
          icon={<Hourglass />}
        />
        <Metric
          label="Attainment, last full month"
          value={pct(latest?.rate ?? null)}
          tone={
            latest?.rate !== undefined && latest.rate !== null && latest.rate < 0.8
              ? 'danger'
              : 'ok'
          }
          hint={
            latest
              ? `${num(latest.met)} of ${num(latest.measured)} closed in ${latest.month}. The month in progress is excluded.`
              : 'No complete month in this window'
          }
          icon={<Scale />}
        />
        <Metric
          label="Time spent waiting"
          value={hasPauseData ? pct(pausedPct) : '—'}
          hint={
            hasPauseData
              ? `${num(data.pausedShare.withPause)} of ${num(data.pausedShare.resolved)} resolved tickets were blocked at some point`
              : 'Not available for imported tickets — FreshService records a single "Pending" state and no pause duration, which is the gap §4.1 exists to close'
          }
          icon={<Hourglass />}
        />
      </div>

      <Panel
        title="Attainment over time"
        subtitle="Share of resolved tickets that met their target, by month"
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            A single attainment figure over six months averages away every trend in it. This is the
            same measure, month by month, so a change in the target or in staffing shows up as a
            step rather than disappearing into the mean.
          </p>
        }
      >
        <LineChart
          points={data.monthlyAttainment
            .filter((m) => m.measured > 0 && !isPartial(m.month))
            .map((m) => ({ x: m.month, y: Math.round((m.rate ?? 0) * 100) }))}
          valueLabel={(v) => `${v}%`}
        />
      </Panel>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Resolution spread by request type"
          subtitle="The whole distribution, not just the middle"
          footer={
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              The gap between p50 and p95 is the number that matters. A type with a two-hour median
              and a three-week p95 is not a two-hour type — it is two different kinds of work
              sharing one label.
            </p>
          }
        >
          <DataTable
            head={['Request type', 'n', 'p50', 'p75', 'p90', 'p95', 'Worst']}
            rows={data.byRequestType.map((r) => [
              shortRequestType(r.label),
              num(r.n),
              hours(r.p50),
              hours(r.p75),
              hours(r.p90),
              <span
                key="p95"
                className={cn(
                  r.p50 !== null && r.p95 !== null && r.p95 > r.p50 * 10 && 'font-semibold text-warn',
                )}
              >
                {hours(r.p95)}
              </span>,
              hours(r.max),
            ])}
          />
        </Panel>

        <Panel title="Resolution spread by queue" subtitle="The same view, per queue">
          <DataTable
            head={['Queue', 'n', 'p50', 'p75', 'p90', 'p95']}
            rows={data.byQueue.map((r) => [
              r.label,
              num(r.n),
              hours(r.p50),
              hours(r.p75),
              hours(r.p90),
              hours(r.p95),
            ])}
          />
        </Panel>
      </div>

      <Panel
        title="First response against resolution"
        subtitle="Two separate promises, measured separately"
      >
        <PairedBars
          rows={data.monthlyAttainment
            .filter((m) => (m.measured > 0 || m.firstResponseMeasured > 0) && !isPartial(m.month))
            .map((m) => ({
              label: m.month.slice(2),
              a: m.firstResponseRate === null ? null : Math.round(m.firstResponseRate * 100),
              b: m.rate === null ? null : Math.round(m.rate * 100),
            }))}
          labelA="First response met"
          labelB="Resolution met"
          format={(v) => `${v}%`}
        />
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recurrence
// ---------------------------------------------------------------------------

export function RecurrenceTab({ data }: { data: RecurrenceReport }) {
  const worst = [...data.repeatByType].sort((a, b) => b.perRequester - a.perRequester)[0];
  const repeatTotal = data.repeatByType.reduce((a, t) => a + t.repeatRequesters, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric
          label="Repeat requesters"
          value={num(repeatTotal)}
          hint="People who came back for the same kind of thing at least twice"
          icon={<Repeat />}
        />
        <Metric
          label="Most repeated ask"
          value={worst ? worst.perRequester.toFixed(1) : '—'}
          tone={worst && worst.perRequester > 2 ? 'warn' : 'default'}
          hint={worst ? `${worst.requestType} — tickets per requester` : undefined}
          icon={<AlertTriangle />}
        />
        <Metric
          label="Inside 30 days"
          value={num(data.pairs.reduce((a, p) => a + p.occurrences, 0))}
          hint="Same person, same problem, within a month — the fix-it-once list"
          icon={<Clock />}
        />
        <Metric
          label="Heaviest requester"
          value={data.topRequesters[0] ? num(data.topRequesters[0].tickets) : '—'}
          hint={
            data.topRequesters[0]
              ? personLabel(data.topRequesters[0].name, data.topRequesters[0].email)
              : undefined
          }
          icon={<Users />}
        />
      </div>

      <Panel
        title="Which asks come back"
        subtitle="Tickets per distinct requester, by request type"
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            Above about two tickets per requester the type is describing a standing problem rather
            than a series of incidents. That is the case for fixing the underlying thing — or for
            self-service — rather than for answering faster.
          </p>
        }
      >
        <DataTable
          head={['Request type', 'Tickets', 'People', 'Came back', 'Per person', 'Median gap']}
          rows={data.repeatByType.map((t) => [
            shortRequestType(t.requestType),
            num(t.tickets),
            num(t.requesters),
            num(t.repeatRequesters),
            <span key="p" className={cn(t.perRequester > 2 && 'font-semibold text-warn')}>
              {t.perRequester.toFixed(1)}
            </span>,
            t.medianDaysBetween === null ? '—' : days(t.medianDaysBetween),
          ])}
        />
      </Panel>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Same person, same problem, inside 30 days"
          subtitle="Each row is a recurrence somebody could have prevented"
        >
          {data.pairs.length === 0 ? (
            <p className="py-6 text-center text-xs text-ink-tertiary">
              Nothing recurred inside a month in this window.
            </p>
          ) : (
            <DataTable
              head={['Requester', 'Request type', 'Repeats', 'Median gap']}
              rows={data.pairs.map((p) => [
                p.email ?? '—',
                shortRequestType(p.requestType),
                num(p.occurrences),
                p.medianDaysBetween === null ? '—' : days(p.medianDaysBetween),
              ])}
            />
          )}
        </Panel>

        <Panel
          title="Who raises the most"
          subtitle="Not a naughty list — a training, tooling or access signal"
        >
          <DataTable
            head={['Person', 'Department', 'Tickets', 'Kinds', 'Most common']}
            rows={data.topRequesters.map((r) => [
              <Tooltip key="p" content={r.email ?? 'unknown'}>
                <span className="flex items-center gap-1.5">
                  <Avatar name={r.name} email={r.email} size="xs" />
                  <span className="truncate">{personLabel(r.name, r.email)}</span>
                </span>
              </Tooltip>,
              r.department ?? '—',
              num(r.tickets),
              num(r.distinctTypes),
              r.topType ? shortRequestType(r.topType) : '—',
            ])}
          />
        </Panel>
      </div>
    </div>
  );
}
